import React, { useRef } from 'react'
import * as yup from 'yup'

import { Formik, FormikProps } from 'formik'
import {
  WorkspaceMinimalFragment,
  ProjectMembershipFragment,
  WorkspaceMembershipFragment,
} from '@sketch/gql-types'
import { useFlag, InvitePeopleField } from '@sketch/modules-common'
import { AddProjectMemberPermissionsAccessSelect } from 'modules/projects/components/ProjectMemberPermissionsAccessSelect/ProjectMemberPermissionsAccessSelect'
import {
  FormValues,
  useAutocomplete,
} from '../AddNewProjectModal/useAutocomplete'
import { InviteButton, MemberInviteSection } from './ProjectSharingModal.styles'

// Form Schema
const FORM_INITIAL_VALUES: FormValues = {
  email: '',
  workspaceMembershipIdentifier: '',
  shouldValidateEmail: true,
  guestCommentsEnabled: true,
  guestInspectEnabled: true,
}

const memberFormSchema = yup.object().shape({
  shouldValidateEmail: yup.boolean(),
  email: yup.string().max(255).when('shouldValidateEmail', {
    is: true,
    then: yup.string().trim().required().lowercase().email(),
  }),
  workspaceMembershipIdentifier: yup.string().trim().nullable(),
  guestCommentsEnabled: yup.boolean(),
  guestInspectEnabled: yup.boolean(),
})

interface FormikRenderProps {
  formikBag: FormikProps<typeof FORM_INITIAL_VALUES>
  projectIdentifier: string
  workspaceIdentifier: string
  membersList: WorkspaceMembershipFragment[]
}

interface AddMemberFormProps {
  workspace: WorkspaceMinimalFragment
  membersList: WorkspaceMembershipFragment[]
  onMemberAdd: (values: FormValues) => void
  projectIdentifier: string
}

export const AddMemberForm = ({
  workspace,
  projectIdentifier,
  membersList,
  onMemberAdd,
}: AddMemberFormProps) => (
  <Formik
    initialValues={FORM_INITIAL_VALUES}
    onSubmit={async (values, { resetForm }) => {
      await onMemberAdd(values)
      resetForm()
    }}
    validateOnChange={true}
    validateOnMount={true}
    validationSchema={memberFormSchema}
  >
    {formikBag => (
      <form
        onSubmit={e => {
          e.preventDefault()
        }}
      >
        <FormikRender
          formikBag={formikBag}
          membersList={membersList}
          projectIdentifier={projectIdentifier}
          workspaceIdentifier={workspace.identifier}
        />
      </form>
    )}
  </Formik>
)

const FormikRender = ({
  formikBag,
  workspaceIdentifier,
  projectIdentifier,
  membersList,
}: FormikRenderProps) => {
  const isGuestsInProjectEnabled = useFlag('guests-in-project')
  const containerRef = useRef<HTMLInputElement>(null)
  const autocomplete = useAutocomplete({
    projectIdentifier,
    workspaceIdentifier,
    membersList: membersList
      .filter(member => Boolean(member.identifier))
      .map(member => member.identifier) as string[],
  })

  return (
    <MemberInviteSection>
      <InvitePeopleField
        containerRef={containerRef}
        autocomplete={autocomplete}
        placeholder="Add a name or email address"
        label="Manage Access"
        dynamicAction={props => {
          if (!isGuestsInProjectEnabled) {
            return
          }

          const workspaceMembership = props.selectedItem as Partial<WorkspaceMembershipFragment>
          const membership = {
            workspaceMembership: workspaceMembership ?? {
              __typename: 'WorkspaceMembership',
              role: 'GUEST',
            },
            privateAccessLevel: workspaceMembership?.isEditor
              ? ('EDIT' as ProjectMembershipFragment['privateAccessLevel'])
              : ('VIEW' as ProjectMembershipFragment['privateAccessLevel']),
          }

          return (
            <AddProjectMemberPermissionsAccessSelect
              member={membership}
              guestCommentsEnabled={
                autocomplete.formik.values.guestCommentsEnabled
              }
              guestInspectEnabled={
                autocomplete.formik.values.guestInspectEnabled
              }
              onPermissionChange={(permission, value) => {
                autocomplete.formik.setFieldValue(permission, value)
              }}
            />
          )
        }}
      />
      <InviteButton
        type="submit"
        name="Add member to project"
        disabled={!autocomplete.formik.isValid}
        onClick={async () => {
          // Form is submitted here instead of <form />
          // because we need to reset the Combobox, and
          // the autocomplete is created in this component
          await formikBag.submitForm()
          autocomplete.combobox.reset()
        }}
      >
        Add
      </InviteButton>
    </MemberInviteSection>
  )
}
